import { withStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React from "react";

import {
  COLOR_KICK_LIGHT_YELLOW,
  COLOR_KICK_NAVY,
} from "~/shared/sharedConstants.js";
import TypewriterText from "./typewriterText.jsx";

// Quiet callout for the realtime AI answer check (see
// docs/pa-chief-complaint-realtime-check.md). Two states — "reading" while the
// check is in flight, "suggestion" for the fixed retry message (the AI only
// validates; the copy is ours) — and deliberately no success state: a
// sufficient answer advances the questionnaire, so absence of the callout is
// the success signal. The reading state sits directly on the indigo surface
// behind a thin left rule, a typographic step below the question; contrast
// ratios of its tokens are measured against the indigo surface #413B79
// (body 7.0:1, label 5.5:1, rule/spark 5.0:1) — do not lower them. The
// suggestion state is a light-yellow note with navy text (#1e2347 on
// #fff7e5 ≈ 14:1) so the retry message stands apart from the indigo copy
// around it.
const styles = {
  coach: {
    marginTop: "20px",
    borderLeft: "2px solid #F0A97F",
    maxWidth: "58ch",
    textAlign: "left",
    animation: "$coachRise .26s cubic-bezier(.2,.7,.3,1) both",
  },
  reading: {
    padding: "1px 0 1px 18px",
  },
  suggestion: {
    padding: "14px 18px",
    background: COLOR_KICK_LIGHT_YELLOW,
    borderRadius: "0 8px 8px 0",
  },
  label: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    fontSize: "11px",
    letterSpacing: ".1em",
    textTransform: "uppercase",
    color: "#C2C0D4",
    marginBottom: "6px",
  },
  spark: {
    color: "#F0A97F",
    flex: "0 0 12px",
    display: "block",
  },
  body: {
    margin: 0,
    fontSize: "15px",
    lineHeight: 1.58,
    color: "#D9D8E4",
  },
  suggestionBody: {
    color: COLOR_KICK_NAVY,
  },
  dots: {
    display: "inline-flex",
    gap: "4px",
    alignItems: "center",
    verticalAlign: "middle",
    marginLeft: "2px",
    "& i": {
      width: "5px",
      height: "5px",
      borderRadius: "50%",
      background: "currentColor",
      opacity: 0.35,
      animation: "$coachPulse 1.15s infinite ease-in-out",
    },
    "& i:nth-child(2)": {
      animationDelay: ".16s",
    },
    "& i:nth-child(3)": {
      animationDelay: ".32s",
    },
  },
  "@keyframes coachRise": {
    from: { opacity: 0, transform: "translateY(-5px)" },
    to: { opacity: 1, transform: "none" },
  },
  "@keyframes coachPulse": {
    "0%, 70%, 100%": { opacity: 0.25 },
    "35%": { opacity: 0.95 },
  },
  "@media (prefers-reduced-motion: reduce)": {
    coach: { animation: "none" },
    dots: { "& i": { animation: "none" } },
  },
};

function AiCoachCallout({ classes, state, text }) {
  return (
    <div
      className={`${classes.coach} ${
        state === "reading" ? classes.reading : classes.suggestion
      }`}
    >
      {state === "reading" ? (
        <>
          <span className={classes.label}>
            <svg
              className={classes.spark}
              viewBox="0 0 12 12"
              fill="currentColor"
              width="12"
              height="12"
              aria-hidden="true"
            >
              <path d="M6 0l1.4 3.4L11 5 7.4 6.3 6 12 4.6 6.3 1 5l3.6-1.6z" />
            </svg>
            Checking your answer
          </span>
          <p className={classes.body}>
            Reading your answer{" "}
            <span className={classes.dots} aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          </p>
        </>
      ) : (
        <p className={`${classes.body} ${classes.suggestionBody}`}>
          <TypewriterText text={text} />
        </p>
      )}
    </div>
  );
}

AiCoachCallout.propTypes = {
  classes: PropTypes.object.isRequired,
  state: PropTypes.oneOf(["reading", "suggestion"]).isRequired,
  // The fixed retry message; required only in the "suggestion" state.
  text: PropTypes.string,
};

export default withStyles(styles)(AiCoachCallout);
